import React, { useMemo, useState } from "react";
import {
    useGetAdminFriendlyPaySettingsQuery,
    useUpdateAdminFriendlyPaySettingsMutation,
    useGetAdminFriendlyPayStatsQuery,
    useGetAdminFriendlyPayPaymentsQuery,
    useGetAdminFriendlyPayPaymentQuery,
} from "../api";
import type { FriendlyPayStatus } from "../api/friendlypay";

const STATUS_LABELS: Record<FriendlyPayStatus, string> = {
    created: "Создан",
    initialized: "Ожидание оплаты",
    success: "Успешно",
    failed: "Ошибка",
    expired: "Истёк",
    cancelled: "Отменён",
};

const STATUS_BADGE: Record<FriendlyPayStatus, string> = {
    created: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
    initialized: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    success: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
    failed: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    expired: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    cancelled: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
};

const fmtDate = (v: string | null): string =>
    v ? new Date(v).toLocaleString("ru-RU") : "—";

export const AdminFriendlyPayPage: React.FC = () => {
    const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);

    const {
        data: settingsResponse,
        isLoading: isLoadingSettings,
        refetch: refetchSettings,
    } = useGetAdminFriendlyPaySettingsQuery();

    const {
        data: statsResponse,
        isLoading: isLoadingStats,
        refetch: refetchStats,
    } = useGetAdminFriendlyPayStatsQuery();

    const [updateSettings, { isLoading: isUpdatingSettings }] =
        useUpdateAdminFriendlyPaySettingsMutation();

    const [page, setPage] = useState(1);
    const [userId, setUserId] = useState("");
    const [status, setStatus] = useState<"" | FriendlyPayStatus>("");
    const [transactionId, setTransactionId] = useState("");
    const [credited, setCredited] = useState<"" | "1" | "0">("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const params = useMemo(
        () => ({
            page,
            per_page: 15,
            sort_by: "created_at" as const,
            sort_order: "desc" as const,
            user_id: userId ? parseInt(userId) : undefined,
            status: status || undefined,
            transaction_id: transactionId || undefined,
            credited: credited === "" ? undefined : credited === "1",
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
        }),
        [page, userId, status, transactionId, credited, dateFrom, dateTo],
    );

    const {
        data: paymentsResponse,
        isLoading,
        refetch,
    } = useGetAdminFriendlyPayPaymentsQuery(params);

    const payments = paymentsResponse?.data ?? [];
    const meta = paymentsResponse?.meta;

    const { data: detailResponse, isLoading: isLoadingDetail } =
        useGetAdminFriendlyPayPaymentQuery(selectedPaymentId!, {
            skip: selectedPaymentId === null,
        });
    const detail = detailResponse?.data;
    const enabled = settingsResponse?.data?.enabled ?? true;
    const stats = statsResponse?.data;

    const handleToggleEnabled = async () => {
        try {
            await updateSettings({ enabled: !enabled }).unwrap();
            refetchSettings();
        } catch (error: any) {
            alert(`Ошибка: ${error?.data?.message || error?.message || "Не удалось сохранить настройку"}`);
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        FriendlyPay
                    </h1>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Платежи через СБП (RUB → USDT)
                    </p>
                </div>
                <button
                    onClick={() => {
                        refetchSettings();
                        refetchStats();
                        refetch();
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    Обновить
                </button>
            </div>

            {/* Settings and Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Статус метода
                            </div>
                            <div className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                                {isLoadingSettings
                                    ? "Загрузка..."
                                    : enabled
                                      ? "FriendlyPay включён"
                                      : "FriendlyPay отключён"}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={handleToggleEnabled}
                            disabled={isLoadingSettings || isUpdatingSettings}
                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                                enabled ? "bg-green-600" : "bg-gray-300 dark:bg-gray-600"
                            }`}
                            aria-label="Переключить FriendlyPay"
                        >
                            <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                    enabled ? "translate-x-5" : "translate-x-0"
                                }`}
                            />
                        </button>
                    </div>
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Управляет показом метода и созданием новых платежей.
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Зачислено USDT
                    </div>
                    <div className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                        {isLoadingStats ? "Загрузка..." : `${stats?.total_payments || "0.00000000"} USDT`}
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Оборот RUB
                    </div>
                    <div className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                        {isLoadingStats ? "Загрузка..." : `${stats?.total_rub || "0.00"} RUB`}
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Транзакции
                    </div>
                    <div className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                        {isLoadingStats ? "Загрузка..." : (stats?.total_transactions ?? 0)}
                    </div>
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Успешно: {stats?.success_transactions ?? 0} · В ожидании: {stats?.pending_transactions ?? 0} · Ошибки: {stats?.failed_transactions ?? 0}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            ID пользователя
                        </label>
                        <input
                            type="number"
                            value={userId}
                            onChange={(e) => {
                                setUserId(e.target.value);
                                setPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                            placeholder="Например 123"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Статус
                        </label>
                        <select
                            value={status}
                            onChange={(e) => {
                                setStatus(e.target.value as "" | FriendlyPayStatus);
                                setPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Все</option>
                            {(Object.keys(STATUS_LABELS) as FriendlyPayStatus[]).map((s) => (
                                <option key={s} value={s}>
                                    {STATUS_LABELS[s]}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Transaction ID
                        </label>
                        <input
                            type="text"
                            value={transactionId}
                            onChange={(e) => {
                                setTransactionId(e.target.value);
                                setPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                            placeholder="ULID или часть"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Зачисление
                        </label>
                        <select
                            value={credited}
                            onChange={(e) => {
                                setCredited(e.target.value as "" | "1" | "0");
                                setPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Все</option>
                            <option value="1">Зачислено</option>
                            <option value="0">Не зачислено</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Дата с
                        </label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => {
                                setDateFrom(e.target.value);
                                setPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Дата по
                        </label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => {
                                setDateTo(e.target.value);
                                setPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                {["ID", "Пользователь", "Transaction ID", "Сумма (RUB)", "USDT", "Курс", "Статус", "Зачислено", "Создано", "Действия"].map(
                                    (h) => (
                                        <th
                                            key={h}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase"
                                        >
                                            {h}
                                        </th>
                                    ),
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={10} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                        Загрузка...
                                    </td>
                                </tr>
                            ) : payments.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                        Платежи не найдены
                                    </td>
                                </tr>
                            ) : (
                                payments.map((p) => (
                                    <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{p.id}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                            <div className="font-medium">
                                                {p.user?.surname} {p.user?.name}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                ID {p.user_id} · {p.user?.mobile || "—"}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-900 dark:text-white font-mono">
                                            {p.transaction_id || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{p.amount_rub}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{p.amount_usdt ?? "—"}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{p.rate ?? "—"}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[p.status]}`}>
                                                {STATUS_LABELS[p.status] ?? p.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                            {p.credited_at ? fmtDate(p.credited_at) : "—"}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{fmtDate(p.created_at)}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <button
                                                onClick={() => setSelectedPaymentId(p.id)}
                                                className="px-3 py-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                            >
                                                Детали
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {meta && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Показано {meta.from ?? 0}–{meta.to ?? 0} из {meta.total}
                        </div>
                        <div className="flex gap-2">
                            <button
                                disabled={meta.current_page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                            >
                                Назад
                            </button>
                            <button
                                disabled={meta.current_page >= meta.last_page}
                                onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
                                className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                            >
                                Вперёд
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail modal */}
            {selectedPaymentId !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Детали платежа #{selectedPaymentId}
                            </h2>
                            <button
                                onClick={() => setSelectedPaymentId(null)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="overflow-y-auto p-6 flex-1">
                            {isLoadingDetail ? (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Загрузка...</div>
                            ) : detail ? (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        {[
                                            ["ID", String(detail.id)],
                                            ["User ID", String(detail.user_id)],
                                            ["Merchant Order ID", detail.merchant_order_id],
                                            ["Transaction ID", detail.transaction_id || "—"],
                                            ["Сумма (RUB)", String(detail.amount_rub)],
                                            ["USDT (merchantAmount)", detail.amount_usdt != null ? String(detail.amount_usdt) : "—"],
                                            ["Курс", detail.rate != null ? String(detail.rate) : "—"],
                                            ["Статус", STATUS_LABELS[detail.status] ?? detail.status],
                                            ["Истекает", fmtDate(detail.expire_at)],
                                            ["Зачислено", fmtDate(detail.credited_at)],
                                            ["Зачислено USDT", detail.credited_amount_usdt != null ? String(detail.credited_amount_usdt) : "—"],
                                            ["Создано", fmtDate(detail.created_at)],
                                        ].map(([label, value]) => (
                                            <div key={label}>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</label>
                                                <div className="text-sm text-gray-900 dark:text-white break-all font-mono">{value}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {detail.user && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Пользователь</h3>
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {detail.user.surname} {detail.user.name} · {detail.user.email || "—"} · {detail.user.mobile || "—"}
                                            </div>
                                        </div>
                                    )}

                                    {detail.qr_link && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">QR-ссылка (СБП)</h3>
                                            <a href={detail.qr_link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 dark:text-blue-400 break-all underline">
                                                {detail.qr_link}
                                            </a>
                                        </div>
                                    )}

                                    {detail.processing_data && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">processing_data</h3>
                                            <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-md text-xs overflow-x-auto">
                                                {JSON.stringify(detail.processing_data, null, 2)}
                                            </pre>
                                        </div>
                                    )}

                                    {detail.raw_webhook && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">raw_webhook</h3>
                                            <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-md text-xs overflow-x-auto">
                                                {JSON.stringify(detail.raw_webhook, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Не удалось загрузить детали</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminFriendlyPayPage;
